// Checks-in-flight widget — money walking the IINK lifecycle (Ship 2).
//
// Mirrors the Payments tab's IINK-path rule exactly so the numbers reconcile
// when Shannon taps through: iink path = `iink_required || state ∈ the IINK
// lifecycle states` (the Aug 4 lesson — never bucket by the boolean alone,
// and a bare `collected` personal check is NOT an IINK check).
// Registry-gated to finance/admin/sales_director (dp_read covers them).

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { logCardClick } from "../../../lib/uiEvents";

const IN_FLIGHT_STATES = [
  "collected",
  "iink_submitted",
  "mortgage_endorsed",
  "fedex_out",
  "fedex_back",
  "iink_cleared",
  "deposited",
];
// States that make a check IINK-path regardless of the iink_required flag —
// same set FinanceDashboard's Payments tab uses.
const IINK_LIFECYCLE = new Set([
  "iink_submitted",
  "mortgage_endorsed",
  "fedex_out",
  "fedex_back",
  "iink_cleared",
]);

export default function IinkWidget() {
  const [view, setView] = useState<{ n: number; total: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("deal_payments")
        .select("id,amount,state,iink_required")
        .in("state", IN_FLIGHT_STATES);
      if (cancelled) return;
      if (!error) {
        const rows = ((data as Array<{ amount: number | null; state: string; iink_required: boolean | null }>) ?? [])
          .filter((r) => r.iink_required || IINK_LIFECYCLE.has(r.state));
        setView({
          n: rows.length,
          total: rows.reduce((s, r) => s + Number(r.amount ?? 0), 0),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <span>📮</span>
        <span className="font-bold text-sm text-bardin-ink">Checks in flight</span>
      </div>
      {!view ? (
        <div className="h-9 rounded-lg bg-slate-100 animate-pulse mt-1" />
      ) : (
        <>
          <div className="text-2xl font-extrabold text-bardin-ink leading-tight">{view.n}</div>
          <div className="text-[11px] text-slate-500 font-semibold">
            walking the IINK lifecycle · ${Math.round(view.total).toLocaleString()}
          </div>
        </>
      )}
      <div className="flex mt-2 pt-2 border-t border-slate-100">
        <Link
          to="/finance?tab=payments"
          onClick={() => logCardClick("widget_iink_open")}
          className="ml-auto text-[11px] font-bold text-bardin-orange"
        >
          Payments ›
        </Link>
      </div>
    </div>
  );
}
